import classNames from "classnames";
import { useState, useRef, useEffect } from "react";

export const EditableField = ({
  value,
  submit,
  placeholder = "--",
  className = "",
  mode = "edit",
}: {
  value: string;
  submit: (value: string) => void;
  placeholder?: string;
  className?: string;
  mode?: "edit" | "new";
}) => {
  const [currentValue, setCurrentValue] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep currentValue in sync with value prop in edit mode
  useEffect(() => {
    if (mode === "edit") {
      setCurrentValue(value ?? "");
    }
  }, [value, mode]);

  const handleSubmit = async () => {
    if (currentValue !== value && currentValue.trim() !== "") {
      setSaving(true);
      await submit(currentValue);
      setSaving(false);
      if (mode === "new") {
        setCurrentValue("");
        // Keep focus for new mode
        if (inputRef.current) inputRef.current.focus();
      } else {
        // Lose focus for edit mode
        if (inputRef.current) inputRef.current.blur();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      setCurrentValue(value ?? "");
      if (inputRef.current) inputRef.current.blur();
    }
  };

  const handleBlur = () => {
    if (mode === "edit") {
      handleSubmit();
    }
    // In "new" mode, do not submit on blur
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className={classNames(className, "editable-input")}
      value={currentValue}
      placeholder={placeholder}
      onBlur={handleBlur}
      onChange={(e) => setCurrentValue(e.target.value)}
      onKeyDown={handleKeyDown}
      disabled={saving}
      spellCheck={false}
      autoComplete="off"
    />
  );
};
